import React, { useEffect, useRef, useState } from "react";
import AdminPageHeader from "@/components/admin/AdminPageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    Plus,
    Trash2,
    Loader2,
    Users,
    Search,
    Pencil,
    Send,
    Upload,
    Download,
    Eye,
    Filter,
    UserCheck,
    Power,
    ArrowLeft,
    ArrowRight,
    ChevronsLeft,
    ChevronsRight,
    X,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "@/lib/api";
import { to12h, to24h, toDdMmYyyy, toIsoDate } from "@/lib/dateFormat";
import { PhoneInput } from "@/components/form/PhoneOtpInputs";

const ANY = "__any__";

const emptyDraft = () => ({
    id: null,
    name: "",
    phone: "",
    email: "",
    college_name: "",
    course: "",
    branch: "",
    role: "",
    schedule_date: "",
    schedule_time: "",
    location_url: "",
});

const emptyFilters = () => ({
    search: "",
    schedule_date: "",
    role: ANY,
    college: ANY,
    whatsapp_status: ANY,
    active_status: ANY,
    attendance_status: ANY,
});

const PAGE_SIZES = [30, 60, 100, 250, 500];

function fmtDateTime(v) {
    // Storage is already canonical "DD-MM-YYYY hh:mm AM/PM". Fall back to
    // Date parse for legacy ISO strings.
    if (!v) return "—";
    const s = String(v).trim();
    if (/^\d{2}-\d{2}-\d{4}\s+\d{2}:\d{2}\s+(AM|PM)$/i.test(s)) return s;
    try {
        const d = new Date(s);
        if (Number.isNaN(d.getTime())) return s;
        const dd = String(d.getDate()).padStart(2, "0");
        const mo = String(d.getMonth() + 1).padStart(2, "0");
        const yy = d.getFullYear();
        let h = d.getHours();
        const mer = h < 12 ? "AM" : "PM";
        h = h % 12 || 12;
        const mm = String(d.getMinutes()).padStart(2, "0");
        return `${dd}-${mo}-${yy} ${String(h).padStart(2, "0")}:${mm} ${mer}`;
    } catch {
        return s;
    }
}

const TEMPLATE_HEADERS = [
    "Name",
    "Phone",
    "Email",
    "College Name",
    "Course",
    "Branch",
    "Role",
    "Schedule Date",
    "Schedule Time",
    "Location",
];

const TEMPLATE_SAMPLE_ROW = [
    "Priya Sharma",
    "9384087147",
    "priya@example.com",
    "IIT Madras",
    "B.Tech",
    "Computer Science",
    "Frontend Developer",
    "20-02-2026",
    "10:30 AM",
    "https://meet.google.com/xyz-abcd-efg",
];

function downloadTemplateCsv() {
    const escapeCell = (v) => {
        const s = String(v ?? "");
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv =
        TEMPLATE_HEADERS.map(escapeCell).join(",") +
        "\n" +
        TEMPLATE_SAMPLE_ROW.map(escapeCell).join(",") +
        "\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "candidates_template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

export default function ManageCandidates() {
    const [candidates, setCandidates] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(30);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState(emptyFilters());
    const [facets, setFacets] = useState({ roles: [], colleges: [] });
    const [selected, setSelected] = useState(new Set());

    // Dialogs
    const [formOpen, setFormOpen] = useState(false);
    const [draft, setDraft] = useState(emptyDraft());
    const [submitting, setSubmitting] = useState(false);

    const [uploadOpen, setUploadOpen] = useState(false);
    const fileInputRef = useRef(null);
    const [uploadResult, setUploadResult] = useState(null);
    const [uploading, setUploading] = useState(false);

    const [viewOpen, setViewOpen] = useState(false);
    const [viewCand, setViewCand] = useState(null);
    const [viewLogs, setViewLogs] = useState([]);

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

    const [sendCand, setSendCand] = useState(null);
    const [sendRegenerate, setSendRegenerate] = useState(false);
    const [sending, setSending] = useState(false);

    const [bulkSendOpen, setBulkSendOpen] = useState(false);
    const [bulkRegenerate, setBulkRegenerate] = useState(false);

    const fetchAll = async () => {
        try {
            const params = {
                paginated: true,
                limit: pageSize,
                skip: (page - 1) * pageSize,
            };
            if (filters.search.trim()) params.search = filters.search.trim();
            if (filters.schedule_date)
                params.schedule_date = toDdMmYyyy(filters.schedule_date);
            if (filters.role !== ANY) params.role = filters.role;
            if (filters.college !== ANY) params.college = filters.college;
            if (filters.whatsapp_status !== ANY)
                params.whatsapp_status = filters.whatsapp_status;
            if (filters.active_status !== ANY)
                params.active_status = filters.active_status;
            if (filters.attendance_status !== ANY)
                params.attendance_status = filters.attendance_status;
            const [{ data }, { data: fac }] = await Promise.all([
                api.get("/candidates", { params }),
                api.get("/candidates/facets"),
            ]);
            setCandidates(data.items || []);
            setTotal(data.total || 0);
            setFacets({ roles: fac.roles || [], colleges: fac.colleges || [] });
        } catch (e) {
            toast.error(apiErrorMessage(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
    }, [page, pageSize]);

    const applyFilters = () => {
        setLoading(true);
        setSelected(new Set());
        setPage(1);
        fetchAll();
    };

    const resetFilters = () => {
        setFilters(emptyFilters());
        setLoading(true);
        setSelected(new Set());
        setPage(1);
        setTimeout(() => fetchAll(), 0);
    };

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    const allSelectedOnPage = candidates.length > 0 && candidates.every((c) => selected.has(c.id));

    const toggleAll = () => {
        if (allSelectedOnPage) {
            setSelected(new Set());
        } else {
            setSelected(new Set(candidates.map((c) => c.id)));
        }
    };

    const toggleOne = (id) => {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelected(next);
    };

    // ---------- Add / Edit ----------
    const openAdd = () => {
        setDraft(emptyDraft());
        setFormOpen(true);
    };
    const openEdit = (c) => {
        setDraft({
            id: c.id,
            name: c.name || "",
            phone: c.phone || "",
            email: c.email || "",
            college_name: c.college_name || "",
            course: c.course || "",
            branch: c.branch || "",
            role: c.role || "",
            // native <input type=date/time> uses ISO/24h — convert from stored canonical
            schedule_date: toIsoDate(c.schedule_date) || "",
            schedule_time: to24h(c.schedule_time) || "",
            location_url: c.location_url || "",
        });
        setFormOpen(true);
    };

    const saveDraft = async () => {
        setSubmitting(true);
        try {
            const payload = { ...draft };
            delete payload.id;
            // Convert draft (ISO / 24h from native inputs) to canonical storage format.
            payload.schedule_date = toDdMmYyyy(draft.schedule_date);
            payload.schedule_time = to12h(draft.schedule_time);
            if (draft.id) {
                await api.put(`/candidates/${draft.id}`, payload);
                toast.success("Candidate updated");
            } else {
                await api.post("/candidates", payload);
                toast.success("Candidate added");
            }
            setFormOpen(false);
            fetchAll();
        } catch (e) {
            toast.error(apiErrorMessage(e));
        } finally {
            setSubmitting(false);
        }
    };

    // ---------- Delete ----------
    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            await api.delete(`/candidates/${deleteTarget.id}`);
            toast.success("Candidate deleted");
            setDeleteTarget(null);
            fetchAll();
        } catch (e) {
            toast.error(apiErrorMessage(e));
        }
    };

    const confirmBulkDelete = async () => {
        try {
            const ids = Array.from(selected);
            const { data } = await api.post("/candidates/bulk-delete", { ids });
            toast.success(`Deleted ${data.deleted} candidate(s)`);
            setBulkDeleteOpen(false);
            setSelected(new Set());
            fetchAll();
        } catch (e) {
            toast.error(apiErrorMessage(e));
        }
    };

    const toggleAttendance = async (c) => {
        const next =
            c.attendance_status === "Attended" ? "Not Attended" : "Attended";
        try {
            await api.post(`/candidates/${c.id}/mark-attendance`, {
                attendance_status: next,
            });
            toast.success(`Marked ${next}`);
            fetchAll();
        } catch (e) {
            toast.error(apiErrorMessage(e));
        }
    };

    const toggleActive = async (c) => {
        const next = c.active_status === "Active" ? "Inactive" : "Active";
        try {
            await api.patch(`/candidates/${c.id}/active`, { active_status: next });
            toast.success(`Marked ${next}`);
            fetchAll();
        } catch (e) {
            toast.error(apiErrorMessage(e));
        }
    };

    // ---------- Send OTP ----------
    const openSend = (c) => {
        setSendCand(c);
        setSendRegenerate(false);
    };

    const confirmSend = async () => {
        if (!sendCand) return;
        setSending(true);
        try {
            await api.post(`/candidates/${sendCand.id}/send-otp`, {
                regenerate: sendRegenerate,
            });
            toast.success("WhatsApp message sent");
            setSendCand(null);
            fetchAll();
        } catch (e) {
            toast.error(apiErrorMessage(e));
        } finally {
            setSending(false);
        }
    };

    const confirmBulkSend = async () => {
        setSending(true);
        try {
            const ids = Array.from(selected);
            const { data } = await api.post("/candidates/bulk-send", {
                ids,
                regenerate: bulkRegenerate,
            });
            toast.success(
                `Sent ${data.success}, failed ${data.failed}. Regenerate=${bulkRegenerate ? "yes" : "no"}`,
            );
            setBulkSendOpen(false);
            setSelected(new Set());
            fetchAll();
        } catch (e) {
            toast.error(apiErrorMessage(e));
        } finally {
            setSending(false);
        }
    };

    // ---------- Upload ----------
    const onUploadFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        setUploadResult(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/candidates/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setUploadResult(data);
            toast.success(
                `Uploaded — created ${data.created}, updated ${data.updated}, skipped ${data.skipped?.length || 0}`,
            );
            fetchAll();
        } catch (err) {
            toast.error(apiErrorMessage(err));
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    // ---------- View details ----------
    const openView = async (c) => {
        setViewCand(c);
        setViewOpen(true);
        setViewLogs([]);
        try {
            const { data } = await api.get(`/candidates/${c.id}/logs`);
            setViewLogs(data);
        } catch {
            /* ignore */
        }
    };

    // ---------- Render helpers ----------
    const statusPill = (c) => {
        if (c.whatsapp_sent) {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                    Sent
                </span>
            );
        }
        if (c.last_status === "Failed") {
            return (
                <span
                    title={c.last_error_message || "Last send failed — will retry."}
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium bg-red-50 text-red-700 border border-red-200"
                >
                    Failed
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
                Pending
            </span>
        );
    };

    const selectedCount = selected.size;

    return (
        <div data-testid="admin-candidates-page">
            <AdminPageHeader
                eyebrow="Candidate WhatsApp"
                title="Candidate WhatsApp OTP"
                description="Upload candidates, schedule WhatsApp messages, and manually resend OTPs. Automation runs every 15 minutes in IST for candidates scheduled today."
                actions={
                    <>
                        <button
                            onClick={() => setUploadOpen(true)}
                            data-testid="btn-open-upload"
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border border-[#0F1F38]/20 hover:border-[#0F1F38] text-[#0F1F38] transition-colors"
                        >
                            <Upload size={14} strokeWidth={1.5} />
                            Upload Excel
                        </button>
                        <button
                            onClick={openAdd}
                            data-testid="btn-open-add"
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#0F1F38] text-[#F7F5F0] hover:bg-[#1A3254] transition-colors"
                        >
                            <Plus size={14} strokeWidth={1.5} />
                            Add Candidate
                        </button>
                    </>
                }
            />

            <div className="max-w-7xl mx-auto px-6 md:px-10 py-8">
                {/* Total count + filter reset/apply header */}
                <div
                    data-testid="candidates-toolbar"
                    className="bg-white border border-[#0F1F38]/10 border-b-0 px-4 md:px-5 py-3 flex flex-wrap items-center justify-between gap-3"
                >
                    <div className="text-sm text-[#0F1F38]">
                        <strong data-testid="candidates-total">{total}</strong>{" "}
                        candidate{total === 1 ? "" : "s"}
                        {total > 0 && (
                            <span className="text-xs text-[#0F1F38]/50 ml-2">
                                showing{" "}
                                {candidates.length === 0
                                    ? 0
                                    : (page - 1) * pageSize + 1}
                                –{(page - 1) * pageSize + candidates.length}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            data-testid="btn-filter-reset"
                            onClick={resetFilters}
                            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs border border-[#0F1F38]/15 text-[#0F1F38]/70 hover:border-[#0F1F38]/40"
                        >
                            <X size={12} /> Reset
                        </button>
                        <button
                            data-testid="btn-filter-apply"
                            onClick={applyFilters}
                            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs bg-[#0F1F38] text-[#F7F5F0] hover:bg-[#1A3254]"
                        >
                            <Filter size={12} /> Apply
                        </button>
                    </div>
                </div>
                {/* Filters */}
                <div
                    data-testid="candidates-filter-bar"
                    className="bg-white border border-[#0F1F38]/10 p-4 md:p-5 grid grid-cols-1 md:grid-cols-6 gap-3"
                >
                    <div className="md:col-span-2">
                        <Label className="text-[10px] uppercase tracking-label text-[#0F1F38]/60">
                            Search
                        </Label>
                        <div className="relative">
                            <Search
                                size={14}
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0F1F38]/40"
                            />
                            <Input
                                data-testid="filter-search"
                                value={filters.search}
                                onChange={(e) =>
                                    setFilters({ ...filters, search: e.target.value })
                                }
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") applyFilters();
                                }}
                                className="pl-9"
                                placeholder="Name, email, phone…"
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-label text-[#0F1F38]/60">
                            Schedule Date
                        </Label>
                        <Input
                            data-testid="filter-date"
                            type="date"
                            value={filters.schedule_date}
                            onChange={(e) =>
                                setFilters({ ...filters, schedule_date: e.target.value })
                            }
                        />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-label text-[#0F1F38]/60">
                            Role
                        </Label>
                        <Select
                            value={filters.role}
                            onValueChange={(v) => setFilters({ ...filters, role: v })}
                        >
                            <SelectTrigger data-testid="filter-role">
                                <SelectValue placeholder="Any" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ANY}>Any role</SelectItem>
                                {facets.roles.map((r) => (
                                    <SelectItem key={r} value={r}>
                                        {r}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-label text-[#0F1F38]/60">
                            College
                        </Label>
                        <Select
                            value={filters.college}
                            onValueChange={(v) => setFilters({ ...filters, college: v })}
                        >
                            <SelectTrigger data-testid="filter-college">
                                <SelectValue placeholder="Any" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ANY}>Any college</SelectItem>
                                {facets.colleges.map((c) => (
                                    <SelectItem key={c} value={c}>
                                        {c}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-label text-[#0F1F38]/60">
                            WA Status
                        </Label>
                        <Select
                            value={filters.whatsapp_status}
                            onValueChange={(v) =>
                                setFilters({ ...filters, whatsapp_status: v })
                            }
                        >
                            <SelectTrigger data-testid="filter-wa-status">
                                <SelectValue placeholder="Any" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ANY}>Any</SelectItem>
                                <SelectItem value="sent">Sent</SelectItem>
                                <SelectItem value="not_sent">Not Sent</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-label text-[#0F1F38]/60">
                            Attendance
                        </Label>
                        <Select
                            value={filters.attendance_status}
                            onValueChange={(v) =>
                                setFilters({ ...filters, attendance_status: v })
                            }
                        >
                            <SelectTrigger data-testid="filter-attendance">
                                <SelectValue placeholder="Any" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ANY}>Any</SelectItem>
                                <SelectItem value="Attended">Attended</SelectItem>
                                <SelectItem value="Not Attended">Not Attended</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-label text-[#0F1F38]/60">
                            Active
                        </Label>
                        <Select
                            value={filters.active_status}
                            onValueChange={(v) =>
                                setFilters({ ...filters, active_status: v })
                            }
                        >
                            <SelectTrigger data-testid="filter-active">
                                <SelectValue placeholder="Any" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={ANY}>Any</SelectItem>
                                <SelectItem value="Active">Active</SelectItem>
                                <SelectItem value="Inactive">Inactive</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                </div>

                {/* Bulk action bar */}
                {selectedCount > 0 && (
                    <div
                        data-testid="bulk-action-bar"
                        className="mt-4 bg-[#0F1F38] text-[#F7F5F0] px-4 py-3 flex items-center justify-between"
                    >
                        <div className="text-sm">
                            <strong>{selectedCount}</strong> selected
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                data-testid="btn-bulk-send"
                                onClick={() => {
                                    setBulkRegenerate(false);
                                    setBulkSendOpen(true);
                                }}
                                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs bg-white/10 hover:bg-white/20"
                            >
                                <Send size={12} /> Send WhatsApp
                            </button>
                            <button
                                data-testid="btn-bulk-delete"
                                onClick={() => setBulkDeleteOpen(true)}
                                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs bg-red-500/20 hover:bg-red-500/30 text-red-100"
                            >
                                <Trash2 size={12} /> Delete
                            </button>
                        </div>
                    </div>
                )}

                {/* Table */}
                <div className="mt-4 bg-white border border-[#0F1F38]/10">
                    {loading ? (
                        <div className="p-16 flex items-center justify-center text-[#4B5563]">
                            <Loader2 className="animate-spin mr-2" size={18} />
                            Loading candidates…
                        </div>
                    ) : candidates.length === 0 ? (
                        <div
                            data-testid="candidates-empty"
                            className="p-16 text-center text-[#4B5563]"
                        >
                            <Users
                                size={28}
                                className="mx-auto mb-3 text-[#0F1F38]/30"
                                strokeWidth={1.2}
                            />
                            <div className="font-medium text-[#0F1F38]">
                                No candidates yet
                            </div>
                            <p className="text-sm mt-1">
                                Add manually or upload an Excel/CSV to begin.
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table data-testid="candidates-table">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-10">
                                            <Checkbox
                                                data-testid="th-select-all"
                                                checked={allSelectedOnPage}
                                                onCheckedChange={toggleAll}
                                            />
                                        </TableHead>
                                        <TableHead>Name</TableHead>
                                        <TableHead>Phone</TableHead>
                                        <TableHead>Email</TableHead>
                                        <TableHead>College</TableHead>
                                        <TableHead>Role</TableHead>
                                        <TableHead>Schedule</TableHead>
                                        <TableHead>OTP</TableHead>
                                        <TableHead>WA</TableHead>
                                        <TableHead>Attendance</TableHead>
                                        <TableHead>Active</TableHead>
                                        <TableHead>Sent</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {candidates.map((c) => (
                                        <TableRow
                                            key={c.id}
                                            data-testid={`candidate-row-${c.id}`}
                                        >
                                            <TableCell>
                                                <Checkbox
                                                    data-testid={`row-select-${c.id}`}
                                                    checked={selected.has(c.id)}
                                                    onCheckedChange={() => toggleOne(c.id)}
                                                />
                                            </TableCell>
                                            <TableCell className="font-medium">
                                                {c.name}
                                            </TableCell>
                                            <TableCell className="tabular-nums">
                                                {c.phone}
                                            </TableCell>
                                            <TableCell className="text-[#4B5563]">
                                                {c.email}
                                            </TableCell>
                                            <TableCell className="text-[#4B5563]">
                                                {c.college_name || "—"}
                                            </TableCell>
                                            <TableCell>{c.role}</TableCell>
                                            <TableCell className="tabular-nums text-xs">
                                                {c.schedule_date}{" "}
                                                <span className="text-[#0F1F38]/50">
                                                    {c.schedule_time}
                                                </span>
                                            </TableCell>
                                            <TableCell className="font-mono">{c.otp}</TableCell>
                                            <TableCell>{statusPill(c)}</TableCell>
                                            <TableCell>
                                                {c.attendance_status === "Attended" ? (
                                                    <span
                                                        title={
                                                            c.attendance_verified_date
                                                                ? `${c.attendance_verified_date} ${c.attendance_verified_time || ""}`
                                                                : ""
                                                        }
                                                        className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200"
                                                    >
                                                        Attended
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium bg-[#F7F5F0] text-[#0F1F38]/70 border border-[#0F1F38]/15">
                                                        Not Attended
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {c.active_status === "Inactive" ? (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium bg-red-50 text-red-700 border border-red-200">
                                                        Inactive
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                                                        Active
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-xs text-[#4B5563]">
                                                {c.sent_datetime ? fmtDateTime(c.sent_datetime) : "—"}
                                                {c.send_attempts > 0 && (
                                                    <div className="text-[10px] text-[#0F1F38]/50">
                                                        {c.send_attempts} attempt
                                                        {c.send_attempts > 1 ? "s" : ""}
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="inline-flex items-center gap-1">
                                                    <button
                                                        data-testid={`btn-view-${c.id}`}
                                                        onClick={() => openView(c)}
                                                        title="View"
                                                        className="p-1.5 hover:bg-[#0F1F38]/5"
                                                    >
                                                        <Eye size={14} />
                                                    </button>
                                                    <button
                                                        data-testid={`btn-edit-${c.id}`}
                                                        onClick={() => openEdit(c)}
                                                        title="Edit"
                                                        className="p-1.5 hover:bg-[#0F1F38]/5"
                                                    >
                                                        <Pencil size={14} />
                                                    </button>
                                                    <button
                                                        data-testid={`btn-send-${c.id}`}
                                                        onClick={() => openSend(c)}
                                                        title="Send OTP"
                                                        className="p-1.5 hover:bg-emerald-50 text-emerald-700"
                                                    >
                                                        <Send size={14} />
                                                    </button>
                                                    <button
                                                        data-testid={`btn-mark-attended-${c.id}`}
                                                        onClick={() =>
                                                            toggleAttendance(c)
                                                        }
                                                        title={
                                                            c.attendance_status ===
                                                            "Attended"
                                                                ? "Mark as Not Attended"
                                                                : "Mark as Attended"
                                                        }
                                                        className={`p-1.5 hover:bg-blue-50 ${
                                                            c.attendance_status ===
                                                            "Attended"
                                                                ? "text-blue-700"
                                                                : "text-[#0F1F38]/60 hover:text-blue-700"
                                                        }`}
                                                    >
                                                        <UserCheck size={14} />
                                                    </button>
                                                    <button
                                                        data-testid={`btn-delete-${c.id}`}
                                                        onClick={() => setDeleteTarget(c)}
                                                        title="Delete"
                                                        className="p-1.5 hover:bg-red-50 text-red-600"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </div>

                {/* Pagination */}
                <div
                    data-testid="candidates-pagination"
                    className="mt-4 bg-white border border-[#0F1F38]/10 px-4 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
                >
                    <div className="flex items-center gap-3 text-xs text-[#4B5563]">
                        <span>Rows per page</span>
                        <Select
                            value={String(pageSize)}
                            onValueChange={(v) => {
                                setPageSize(parseInt(v, 10));
                                setPage(1);
                            }}
                        >
                            <SelectTrigger
                                data-testid="candidates-page-size"
                                className="w-24"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {PAGE_SIZES.map((n) => (
                                    <SelectItem key={n} value={String(n)}>
                                        {n}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex items-center gap-1">
                        <button
                            data-testid="candidates-pg-first"
                            onClick={() => setPage(1)}
                            disabled={page === 1}
                            className="p-1.5 border border-[#0F1F38]/15 hover:border-[#0F1F38] disabled:opacity-40"
                        >
                            <ChevronsLeft size={14} />
                        </button>
                        <button
                            data-testid="candidates-pg-prev"
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="p-1.5 border border-[#0F1F38]/15 hover:border-[#0F1F38] disabled:opacity-40"
                        >
                            <ArrowLeft size={14} />
                        </button>
                        <div className="px-3 text-xs text-[#0F1F38]/70">
                            Page{" "}
                            <strong data-testid="candidates-page">{page}</strong>{" "}
                            of <strong>{totalPages}</strong>
                        </div>
                        <button
                            data-testid="candidates-pg-next"
                            onClick={() =>
                                setPage((p) => Math.min(totalPages, p + 1))
                            }
                            disabled={page >= totalPages}
                            className="p-1.5 border border-[#0F1F38]/15 hover:border-[#0F1F38] disabled:opacity-40"
                        >
                            <ArrowRight size={14} />
                        </button>
                        <button
                            data-testid="candidates-pg-last"
                            onClick={() => setPage(totalPages)}
                            disabled={page >= totalPages}
                            className="p-1.5 border border-[#0F1F38]/15 hover:border-[#0F1F38] disabled:opacity-40"
                        >
                            <ChevronsRight size={14} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Add / Edit Dialog */}
            <Dialog open={formOpen} onOpenChange={setFormOpen}>
                <DialogContent
                    data-testid="candidate-form-dialog"
                    className="max-w-2xl"
                >
                    <DialogHeader>
                        <DialogTitle>
                            {draft.id ? "Edit Candidate" : "Add Candidate"}
                        </DialogTitle>
                        <DialogDescription>
                            All fields marked with * are required. Location must be a
                            valid URL.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
                        <Field label="Name *" testid="draft-name">
                            <Input
                                value={draft.name}
                                onChange={(e) =>
                                    setDraft({ ...draft, name: e.target.value })
                                }
                            />
                        </Field>
                        <Field label="Phone *" testid="draft-phone">
                            <PhoneInput
                                value={draft.phone}
                                onChange={(v) => setDraft({ ...draft, phone: v })}
                                testid="draft-phone-input"
                                required
                            />
                        </Field>
                        <Field label="Email *" testid="draft-email">
                            <Input
                                type="email"
                                value={draft.email}
                                onChange={(e) =>
                                    setDraft({ ...draft, email: e.target.value })
                                }
                            />
                        </Field>
                        <Field label="Role *" testid="draft-role">
                            <Input
                                value={draft.role}
                                onChange={(e) =>
                                    setDraft({ ...draft, role: e.target.value })
                                }
                            />
                        </Field>
                        <Field label="College Name" testid="draft-college">
                            <Input
                                value={draft.college_name}
                                onChange={(e) =>
                                    setDraft({ ...draft, college_name: e.target.value })
                                }
                            />
                        </Field>
                        <Field label="Course" testid="draft-course">
                            <Input
                                value={draft.course}
                                onChange={(e) =>
                                    setDraft({ ...draft, course: e.target.value })
                                }
                            />
                        </Field>
                        <Field label="Branch" testid="draft-branch">
                            <Input
                                value={draft.branch}
                                onChange={(e) =>
                                    setDraft({ ...draft, branch: e.target.value })
                                }
                            />
                        </Field>
                        <Field label="Schedule Date *" testid="draft-date">
                            <Input
                                type="date"
                                value={draft.schedule_date}
                                onChange={(e) =>
                                    setDraft({ ...draft, schedule_date: e.target.value })
                                }
                            />
                        </Field>
                        <Field label="Schedule Time *" testid="draft-time">
                            <Input
                                type="time"
                                value={draft.schedule_time}
                                onChange={(e) =>
                                    setDraft({ ...draft, schedule_time: e.target.value })
                                }
                            />
                        </Field>
                        <div className="md:col-span-2">
                            <Field label="Location URL *" testid="draft-location">
                                <Input
                                    value={draft.location_url}
                                    onChange={(e) =>
                                        setDraft({
                                            ...draft,
                                            location_url: e.target.value,
                                        })
                                    }
                                    placeholder="https://meet.google.com/xyz"
                                />
                            </Field>
                        </div>
                    </div>
                    <DialogFooter>
                        <button
                            onClick={() => setFormOpen(false)}
                            className="px-4 py-2 text-sm border border-[#0F1F38]/20 hover:border-[#0F1F38]"
                        >
                            Cancel
                        </button>
                        <button
                            data-testid="btn-save-candidate"
                            onClick={saveDraft}
                            disabled={submitting}
                            className="px-4 py-2 text-sm bg-[#0F1F38] text-[#F7F5F0] hover:bg-[#1A3254] disabled:opacity-50 inline-flex items-center gap-2"
                        >
                            {submitting && <Loader2 size={14} className="animate-spin" />}
                            {draft.id ? "Update" : "Add"}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Upload Dialog */}
            <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
                <DialogContent data-testid="upload-dialog" className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Upload Excel / CSV</DialogTitle>
                        <DialogDescription>
                            Accepted headers: name, phone, email, college name, course,
                            branch, role, schedule date, schedule time, location. Existing
                            candidates (matched by phone or email) will be updated.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex items-center justify-between px-1 pt-1">
                        <div className="text-xs text-[#4B5563]">
                            Not sure about the format?
                        </div>
                        <button
                            type="button"
                            data-testid="btn-download-template"
                            onClick={downloadTemplateCsv}
                            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs border border-[#0F1F38]/20 hover:border-[#0F1F38] text-[#0F1F38]"
                        >
                            <Download size={12} strokeWidth={1.5} />
                            Download template
                        </button>
                    </div>
                    <div className="space-y-4 py-2">
                        <label
                            data-testid="upload-drop"
                            className="block border-2 border-dashed border-[#0F1F38]/20 p-8 text-center hover:border-[#0F1F38]/40 cursor-pointer"
                        >
                            <Upload
                                size={24}
                                className="mx-auto mb-3 text-[#0F1F38]/40"
                            />
                            <div className="font-medium text-[#0F1F38]">
                                {uploading ? "Uploading…" : "Choose file"}
                            </div>
                            <div className="text-xs text-[#4B5563] mt-1">
                                .xlsx or .csv
                            </div>
                            <input
                                ref={fileInputRef}
                                data-testid="upload-input"
                                type="file"
                                accept=".xlsx,.csv"
                                className="hidden"
                                onChange={onUploadFile}
                                disabled={uploading}
                            />
                        </label>
                        {uploadResult && (
                            <div
                                data-testid="upload-result"
                                className="border border-[#0F1F38]/10 p-3 text-sm"
                            >
                                <div>
                                    <strong>{uploadResult.created}</strong> created,{" "}
                                    <strong>{uploadResult.updated}</strong> updated,{" "}
                                    <strong>{uploadResult.skipped?.length || 0}</strong>{" "}
                                    skipped (of {uploadResult.total_rows} rows).
                                </div>
                                {uploadResult.skipped?.length > 0 && (
                                    <div className="mt-2 max-h-40 overflow-auto">
                                        <table className="w-full text-xs">
                                            <thead>
                                                <tr className="text-[#0F1F38]/60">
                                                    <th className="text-left">Row</th>
                                                    <th className="text-left">Reason</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {uploadResult.skipped.map((s, i) => (
                                                    <tr key={i}>
                                                        <td className="pr-3">{s.row}</td>
                                                        <td className="text-red-600">
                                                            {s.reason}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* View Details Dialog */}
            <Dialog open={viewOpen} onOpenChange={setViewOpen}>
                <DialogContent
                    data-testid="view-dialog"
                    className="max-w-2xl"
                >
                    <DialogHeader>
                        <DialogTitle>Candidate Details</DialogTitle>
                    </DialogHeader>
                    {viewCand && (
                        <div className="space-y-4 text-sm">
                            <div className="grid grid-cols-2 gap-3">
                                <Info k="Name" v={viewCand.name} />
                                <Info k="Phone" v={viewCand.phone} />
                                <Info k="Email" v={viewCand.email} />
                                <Info k="Role" v={viewCand.role} />
                                <Info k="College" v={viewCand.college_name} />
                                <Info k="Course / Branch" v={`${viewCand.course || "—"} / ${viewCand.branch || "—"}`} />
                                <Info
                                    k="Schedule"
                                    v={`${viewCand.schedule_date} @ ${viewCand.schedule_time}`}
                                />
                                <Info k="OTP" v={viewCand.otp} />
                                <Info
                                    k="Location"
                                    v={
                                        <a
                                            href={viewCand.location_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-blue-600 underline break-all"
                                        >
                                            {viewCand.location_url}
                                        </a>
                                    }
                                />
                                <Info
                                    k="WhatsApp"
                                    v={viewCand.whatsapp_sent ? "Sent" : "Pending"}
                                />
                                <Info k="Attempts" v={String(viewCand.send_attempts)} />
                                <Info
                                    k="Last Sent"
                                    v={fmtDateTime(viewCand.sent_datetime)}
                                />
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-label text-[#0F1F38]/60 mb-2">
                                    Activity Log
                                </div>
                                {viewLogs.length === 0 ? (
                                    <div className="text-[#4B5563] text-xs">No activity yet.</div>
                                ) : (
                                    <div className="border border-[#0F1F38]/10 max-h-56 overflow-auto">
                                        <table className="w-full text-xs">
                                            <thead className="bg-[#F7F5F0] text-[#0F1F38]/60">
                                                <tr>
                                                    <th className="text-left px-3 py-2">Time</th>
                                                    <th className="text-left px-3 py-2">Trigger</th>
                                                    <th className="text-left px-3 py-2">Status</th>
                                                    <th className="text-left px-3 py-2">OTP</th>
                                                    <th className="text-left px-3 py-2">Note</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {viewLogs.map((l) => (
                                                    <tr
                                                        key={l.id}
                                                        className="border-t border-[#0F1F38]/5"
                                                    >
                                                        <td className="px-3 py-2">
                                                            {fmtDateTime(l.sent_time)}
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            {l.trigger_type}
                                                        </td>
                                                        <td
                                                            className={`px-3 py-2 ${
                                                                l.status === "Success"
                                                                    ? "text-emerald-700"
                                                                    : "text-red-600"
                                                            }`}
                                                        >
                                                            {l.status}
                                                        </td>
                                                        <td className="px-3 py-2 font-mono">
                                                            {l.otp}
                                                        </td>
                                                        <td className="px-3 py-2 text-[#4B5563]">
                                                            {l.error_message || "—"}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Delete confirm (single) */}
            <Dialog
                open={!!deleteTarget}
                onOpenChange={(v) => !v && setDeleteTarget(null)}
            >
                <DialogContent data-testid="delete-dialog">
                    <DialogHeader>
                        <DialogTitle>Delete candidate?</DialogTitle>
                        <DialogDescription>
                            {deleteTarget?.name} ({deleteTarget?.email}) will be permanently
                            removed along with their logs metadata.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <button
                            onClick={() => setDeleteTarget(null)}
                            className="px-4 py-2 text-sm border border-[#0F1F38]/20"
                        >
                            Cancel
                        </button>
                        <button
                            data-testid="btn-confirm-delete"
                            onClick={confirmDelete}
                            className="px-4 py-2 text-sm bg-red-600 text-white hover:bg-red-700"
                        >
                            Delete
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Bulk delete */}
            <Dialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
                <DialogContent data-testid="bulk-delete-dialog">
                    <DialogHeader>
                        <DialogTitle>Delete {selectedCount} candidate(s)?</DialogTitle>
                        <DialogDescription>
                            This cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <button
                            onClick={() => setBulkDeleteOpen(false)}
                            className="px-4 py-2 text-sm border border-[#0F1F38]/20"
                        >
                            Cancel
                        </button>
                        <button
                            data-testid="btn-confirm-bulk-delete"
                            onClick={confirmBulkDelete}
                            className="px-4 py-2 text-sm bg-red-600 text-white hover:bg-red-700"
                        >
                            Delete
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Single send OTP */}
            <Dialog open={!!sendCand} onOpenChange={(v) => !v && setSendCand(null)}>
                <DialogContent data-testid="send-dialog">
                    <DialogHeader>
                        <DialogTitle>Send WhatsApp OTP</DialogTitle>
                        <DialogDescription>
                            To <strong>{sendCand?.name}</strong> at {sendCand?.phone}.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex items-center justify-between py-2 border-y border-[#0F1F38]/10 my-2">
                        <div>
                            <div className="text-sm font-medium">Regenerate OTP</div>
                            <div className="text-xs text-[#4B5563]">
                                {sendRegenerate
                                    ? "A new 6-digit OTP will be generated."
                                    : `Reuse existing OTP (${sendCand?.otp}).`}
                            </div>
                        </div>
                        <Switch
                            data-testid="toggle-regenerate"
                            checked={sendRegenerate}
                            onCheckedChange={setSendRegenerate}
                        />
                    </div>
                    <DialogFooter>
                        <button
                            onClick={() => setSendCand(null)}
                            className="px-4 py-2 text-sm border border-[#0F1F38]/20"
                        >
                            Cancel
                        </button>
                        <button
                            data-testid="btn-confirm-send"
                            onClick={confirmSend}
                            disabled={sending}
                            className="px-4 py-2 text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-2"
                        >
                            {sending ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                <Send size={14} />
                            )}
                            Send
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Bulk send */}
            <Dialog open={bulkSendOpen} onOpenChange={setBulkSendOpen}>
                <DialogContent data-testid="bulk-send-dialog">
                    <DialogHeader>
                        <DialogTitle>
                            Send WhatsApp to {selectedCount} candidate(s)
                        </DialogTitle>
                    </DialogHeader>
                    <div className="flex items-center justify-between py-2 border-y border-[#0F1F38]/10 my-2">
                        <div>
                            <div className="text-sm font-medium">Regenerate OTPs</div>
                            <div className="text-xs text-[#4B5563]">
                                {bulkRegenerate
                                    ? "A fresh OTP will be created for each candidate."
                                    : "Existing OTPs will be reused."}
                            </div>
                        </div>
                        <Switch
                            data-testid="toggle-bulk-regenerate"
                            checked={bulkRegenerate}
                            onCheckedChange={setBulkRegenerate}
                        />
                    </div>
                    <DialogFooter>
                        <button
                            onClick={() => setBulkSendOpen(false)}
                            className="px-4 py-2 text-sm border border-[#0F1F38]/20"
                        >
                            Cancel
                        </button>
                        <button
                            data-testid="btn-confirm-bulk-send"
                            onClick={confirmBulkSend}
                            disabled={sending}
                            className="px-4 py-2 text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-2"
                        >
                            {sending ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                <Send size={14} />
                            )}
                            Send All
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Field({ label, testid, children }) {
    const child = React.Children.only(children);
    return (
        <div>
            <Label className="text-[10px] uppercase tracking-label text-[#0F1F38]/60">
                {label}
            </Label>
            {React.cloneElement(child, { "data-testid": testid })}
        </div>
    );
}

function Info({ k, v }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-label text-[#0F1F38]/50">
                {k}
            </div>
            <div className="text-sm text-[#0F1F38] break-words">{v || "—"}</div>
        </div>
    );
}
